import { motion, useInView, animate } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { isPrintMode } from "../lib/printMode";

// Motion tokens — quiet, ease-out only. No spring overshoot anywhere.
const EASE = [0.22, 0.61, 0.36, 1];       // standard ease-out (no bounce)
const DUR_SM = 0.2;                        // 200ms — labels, chips, small elements
const DUR_MD = 0.3;                        // 300ms — body content, section reveals
const DUR_LG = 0.4;                        // 400ms — hero lines, primary content
export const MOTION = { EASE, DUR_SM, DUR_MD, DUR_LG };

// Animates a number from `from` to `to`. Triggers on scroll-into-view, or
// immediately when `startOnMount` is set (useful inside dialogs).
export const CountUp = ({ to, from = 0, prefix = "", suffix = "", duration = 1.6, startOnMount = false, className = "", ...props }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [val, setVal] = useState(from);
  const active = startOnMount || inView;

  useEffect(() => {
    if (!active || isPrintMode) return undefined;
    const controls = animate(from, to, {
      duration,
      ease: EASE,
      onUpdate: (v) => setVal(v),
    });
    return () => controls.stop();
  }, [active, from, to, duration]);

  const shown = isPrintMode ? to : val;

  return (
    <span ref={ref} className={className} {...props}>
      {prefix}
      {Math.round(shown).toLocaleString()}
      {suffix}
    </span>
  );
};

// Brand-palette confetti burst. When `origin` (viewport {x,y}) is provided it
// fires from that point via a body portal (escapes any dialog clipping);
// otherwise it bursts from the center of its positioned parent.
const CONFETTI_COLORS = ["#FDD006", "#FD6106", "#0322F4", "#0A9B06", "#F92408", "#FD98D9", "#8AE0F8"];

export const ConfettiBurst = ({ count = 52, origin = null }) => {
  const pieces = useRef(
    Array.from({ length: count }, (_, i) => {
      const angle = (Math.PI * 2 * i) / count + (Math.random() - 0.5) * 0.6;
      const dist = (origin ? 160 : 110) + Math.random() * (origin ? 260 : 170);
      return {
        id: i,
        x: Math.cos(angle) * dist,
        y: Math.sin(angle) * dist - 40,
        rotate: Math.random() * 720 - 360,
        color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
        w: 6 + Math.random() * 8,
        h: 4 + Math.random() * 6,
        delay: Math.random() * 0.1,
        duration: 1.1 + Math.random() * 0.9,
      };
    })
  ).current;

  const emitter = (
    <div
      className={`pointer-events-none overflow-hidden ${origin ? "fixed inset-0 z-[100]" : "absolute inset-0 z-20"}`}
      aria-hidden="true"
    >
      <div className="absolute" style={origin ? { left: origin.x, top: origin.y } : { left: "50%", top: "50%" }}>
        {pieces.map((p) => (
          <motion.span
            key={p.id}
            className="absolute rounded-[2px]"
            style={{ width: p.w, height: p.h, backgroundColor: p.color }}
            initial={{ x: 0, y: 0, opacity: 1, rotate: 0, scale: 1 }}
            animate={{ x: p.x, y: [p.y, p.y + 160], opacity: [1, 1, 0], rotate: p.rotate, scale: 0.7 }}
            transition={{ duration: p.duration, delay: p.delay, ease: "easeOut" }}
          />
        ))}
      </div>
    </div>
  );

  if (origin && typeof document !== "undefined") return createPortal(emitter, document.body);
  return emitter;
};

export const FadeUp = ({ children, delay = 0, className = "", y = 16, tier = "md" }) => {
  if (isPrintMode) return <div className={className}>{children}</div>;
  const duration = tier === "lg" ? DUR_LG : tier === "sm" ? DUR_SM : DUR_MD;
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration, delay, ease: EASE }}
    >
      {children}
    </motion.div>
  );
};

// Masked line-by-line reveal for headings. `lines` is an array of strings.
export const LineReveal = ({ lines, className = "", stagger = 0.06, delay = 0 }) => {
  if (isPrintMode) {
    return (
      <span className={className}>
        {lines.map((line, i) => (
          <span key={i} className="block">{line}</span>
        ))}
      </span>
    );
  }
  return (
    <span className={className}>
      {lines.map((line, i) => (
        <span key={i} className="block overflow-hidden pb-[0.14em] -mb-[0.14em]">
          <motion.span
            className="block"
            initial={{ y: "110%" }}
            animate={{ y: "0%" }}
            transition={{ duration: DUR_LG, delay: delay + i * stagger, ease: EASE }}
          >
            {line}
          </motion.span>
        </span>
      ))}
    </span>
  );
};
